import { formatNumberToCurrency } from 'utils/app';

import type { JSX } from 'react';

const WEEKDAY_LABELS = ['日曜', '月曜', '火曜', '水曜', '木曜', '金曜', '土曜'];
const HOUR_LABEL_INTERVAL = 3;
const LEGEND_STEPS = [0, 1 / 4, 1 / 2, 3 / 4, 1];

function isValidMatrix(hourlyHeatmap: number[][]): boolean {
  if (hourlyHeatmap.length !== 7) return false;
  return hourlyHeatmap.every(row => Array.isArray(row) && row.length === 24);
}

function cellStyle(count: number, max: number): { backgroundColor: string } {
  if (count === 0) return { backgroundColor: 'var(--color-surface)' };
  const alpha = 0.12 + (count / max) * 0.88;
  return { backgroundColor: `color-mix(in srgb, var(--color-brand) ${alpha * 100}%, transparent)` };
}

/**
 * 時間帯 × 曜日ヒートマップ表示コンポーネント（EV-report-analytics-05）。
 * 受診者が最初にコンテンツを開いた時刻の分布を 7×24 の CSS グリッドで表示する。
 */
export default function WsReportHourlyHeatmap(props: {
  hourlyHeatmap: number[][];
}): JSX.Element | null {
  const { hourlyHeatmap } = props;
  if (!isValidMatrix(hourlyHeatmap)) return null;

  const max = Math.max(...hourlyHeatmap.flat());
  if (max === 0) return null;

  // max が小さいと LEGEND_STEPS を丸めた値が重複してしまう（例: max=1 だと 0/0/1/1/1）ため、
  // 重複を取り除いて実際に区別できる段階数だけを凡例に出す
  const legendValues = Array.from(new Set(LEGEND_STEPS.map(step => Math.round(max * step))));

  return (
    <div className="card card-white behavior-analytics-card">
      <div className="card-body">
        <h4 className="title-section title-sm">時間帯 × 曜日ヒートマップ</h4>
        <div className="behavior-heatmap-wrapper">
          <div
            className="behavior-heatmap-grid"
            style={{ gridTemplateColumns: `repeat(25, minmax(20px, 1fr))` }}
          >
            <div />
            {Array.from({ length: 24 }, (_, hour) => (
              <div key={`hour-label-${hour}`} className="behavior-heatmap-hour-label">
                {hour % HOUR_LABEL_INTERVAL === 0 ? `${hour}時` : ''}
              </div>
            ))}
            {hourlyHeatmap.map((row, weekday) => (
              <div key={`row-${weekday}`} style={{ display: 'contents' }}>
                <div className="behavior-heatmap-row-label">{WEEKDAY_LABELS[weekday]}</div>
                {row.map((count, hour) => (
                  <div
                    key={`cell-${weekday}-${hour}`}
                    className="behavior-heatmap-cell"
                    style={cellStyle(count, max)}
                    title={`${WEEKDAY_LABELS[weekday]} ${hour}時台: ${count} セッション`}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="behavior-heatmap-legend">
          {legendValues.map(value => (
            <span key={value}>
              <span className="behavior-heatmap-legend-swatch" style={cellStyle(value, max)} />
              {formatNumberToCurrency(value)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
